import React from 'react';
import { AiChatMessage } from '../../store/useAiAssistantStore';

interface AiMessageBubbleProps {
  message: AiChatMessage;
}

export const AiMessageBubble: React.FC<AiMessageBubbleProps> = ({ message }) => {
  const isUser = message.role === 'user';

  // Basic Markdown Renderer for Bold, Italic, Code, and Lists
  const renderContent = (text: string) => {
    // This is a simplified regex approach for basic markdown support.
    // In production, consider adding a library like react-markdown if needed.
    
    // Split by newlines first
    const lines = text.split('\n');
    
    return lines.map((line, lineIndex) => {
      // Handle empty lines
      if (!line.trim()) {
        return <br key={`br-${lineIndex}`} />;
      }

      // Handle lists (starts with - or * )
      if (line.match(/^[\-\*]\s+(.*)/)) {
        const listMatch = line.match(/^[\-\*]\s+(.*)/);
        const listText = listMatch ? listMatch[1] : line;
        return (
          <div key={`line-${lineIndex}`} className="flex items-start ml-4 mb-1">
            <span className="mr-2">•</span>
            <span>{processInlineMarkdown(listText)}</span>
          </div>
        );
      }

      // Handle code blocks (simplified)
      if (line.startsWith('```')) {
        // Just skip the marker for this basic renderer
        return null;
      }

      // Normal line
      return (
        <div key={`line-${lineIndex}`} className="mb-2">
          {processInlineMarkdown(line)}
        </div>
      );
    });
  };

  const processInlineMarkdown = (text: string) => {
    // Very basic regex replacements for bold and code
    // A robust solution would use a tokenizer
    let result: React.ReactNode[] = [];
    let currentIndex = 0;

    // A simple regex to find `code` and **bold**
    const regex = /(\*\*.*?\*\*|`.*?`)/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        result.push(<span key={`text-${currentIndex}`}>{text.substring(currentIndex, match.index)}</span>);
      }

      const matchText = match[0];
      if (matchText.startsWith('**') && matchText.endsWith('**')) {
        result.push(<strong key={`bold-${match.index}`} className="font-semibold text-slate-900 dark:text-white">{matchText.substring(2, matchText.length - 2)}</strong>);
      } else if (matchText.startsWith('`') && matchText.endsWith('`')) {
        result.push(
          <code key={`code-${match.index}`} className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-sm font-mono text-slate-800 dark:text-slate-200">
            {matchText.substring(1, matchText.length - 1)}
          </code>
        );
      }

      currentIndex = regex.lastIndex;
    }

    // Add remaining text
    if (currentIndex < text.length) {
      result.push(<span key={`text-${currentIndex}`}>{text.substring(currentIndex)}</span>);
    }

    return result.length > 0 ? result : text;
  };

  return (
    <div className={`flex w-full mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div 
        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
          isUser 
            ? 'bg-primary-500 text-white rounded-br-none' 
            : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-bl-none border border-slate-200 dark:border-slate-700'
        }`}
      >
        <div className="text-sm leading-relaxed">
          {isUser ? message.content : renderContent(message.content)}
        </div>
        <div className={`text-[10px] mt-1 ${isUser ? 'text-primary-200 text-right' : 'text-slate-400'}`}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
};
